import { Bridge } from "@xt/xtbase";
import { isInAppWebView, resolveBehaviorClientType } from "@/utils/resolveBehaviorClientType";

const STORAGE_KEY = "__behavior_app_device_context__";

const BRIDGE_DEVICE_METHODS = [
  "getDeviceInfo",
  "appDeviceInfo",
  "getAppDeviceInfo",
  "deviceInfo",
];

const BRIDGE_MODEL_METHODS = ["getDeviceModel", "deviceModel"];
const BRIDGE_APP_VERSION_METHODS = [
  "getAppVersion",
  "appVersion",
  "appVersionName",
  "getAppVersionName",
];

function safeParseJson(raw, fallback = null) {
  if (!raw) return fallback;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch (err) {
    return fallback;
  }
}

function readStorageContext() {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage?.getItem(STORAGE_KEY) ||
      window.localStorage?.getItem(STORAGE_KEY);
    return safeParseJson(raw, null);
  } catch (err) {
    return null;
  }
}

function writeStorageContext(context) {
  if (typeof window === "undefined" || !context) return;
  try {
    const raw = JSON.stringify(context);
    window.sessionStorage?.setItem(STORAGE_KEY, raw);
    window.localStorage?.setItem(STORAGE_KEY, raw);
  } catch (err) {
    // ignore quota / private mode
  }
}

function readInjectedContext() {
  if (typeof window === "undefined") return null;
  const candidates = [
    window.__BEHAVIOR_APP_CONTEXT__,
    window.__APP_DEVICE_CONTEXT__,
    window.__APP_CONTEXT__,
  ];
  for (const item of candidates) {
    const parsed = safeParseJson(item, item);
    if (parsed && typeof parsed === "object") return parsed;
  }
  return null;
}

function hasNativeMethod(name) {
  try {
    // eslint-disable-next-line global-require
    const dsBridge = require("dsbridge");
    return dsBridge.hasNativeMethod && dsBridge.hasNativeMethod(name);
  } catch (err) {
    return false;
  }
}

function callBridgeMethod(name, data) {
  try {
    if (!hasNativeMethod(name)) return null;
    const result = Bridge.call(name, data);
    return result == null || result === "" ? null : result;
  } catch (err) {
    return null;
  }
}

function callBridgeFirst(methods, data) {
  for (const method of methods) {
    const result = callBridgeMethod(method, data);
    if (result != null && result !== "") return result;
  }
  return null;
}

function normalizeBridgePayload(raw) {
  const data = safeParseJson(raw, raw);
  if (!data || typeof data !== "object") return {};
  return {
    device_model:
      data.device_model ||
      data.deviceModel ||
      data.model ||
      data.device ||
      "",
    app_version_name:
      data.app_version_name ||
      data.appVersionName ||
      data.app_version ||
      data.appVersion ||
      data.version ||
      "",
    os_version:
      data.os_version ||
      data.osVersion ||
      data.systemVersion ||
      data.system_version ||
      "",
    device_os: data.device_os || data.deviceOs || data.platform || "",
  };
}

function readBridgeContext() {
  const merged = {};

  const deviceInfo = callBridgeFirst(BRIDGE_DEVICE_METHODS);
  Object.assign(merged, normalizeBridgePayload(deviceInfo));

  const model = callBridgeFirst(BRIDGE_MODEL_METHODS);
  if (model && !merged.device_model) {
    merged.device_model = String(model);
  }

  const appVersion = callBridgeFirst(BRIDGE_APP_VERSION_METHODS);
  if (appVersion && !merged.app_version_name) {
    merged.app_version_name = String(appVersion);
  }

  return merged;
}

function parseAppVersionFromUa(ua) {
  const match = ua.match(/com\.xt\.app[/ ]([\d.]+)/i);
  return match ? match[1] : "";
}

function parseAndroidFromUa(ua) {
  const osMatch = ua.match(/Android\s+([\d.]+)/i);
  const modelMatch = ua.match(/Android\s+[\d.]+;\s*([^;)]+?)(?:\s+Build\/|\))/i);
  return {
    os_version: osMatch ? osMatch[1] : "",
    device_model: modelMatch ? modelMatch[1].trim() : "",
  };
}

function parseIosFromUa(ua) {
  const osMatch = ua.match(/OS\s+(\d+[_.]\d+(?:[_.]\d+)?)/i);
  let deviceModel = "";
  if (/iPad/i.test(ua)) deviceModel = "iPad";
  else if (/iPhone/i.test(ua)) deviceModel = "iPhone";
  else if (/iPod/i.test(ua)) deviceModel = "iPod";
  return {
    os_version: osMatch ? osMatch[1].replace(/_/g, ".") : "",
    device_model: deviceModel,
  };
}

function parseFromUserAgent() {
  if (typeof navigator === "undefined") return {};
  const ua = navigator.userAgent || "";
  const parsed = {
    app_version_name: parseAppVersionFromUa(ua),
  };

  if (/Android/i.test(ua)) {
    Object.assign(parsed, parseAndroidFromUa(ua));
  } else if (/(iPhone|iPad|iPod)/i.test(ua)) {
    Object.assign(parsed, parseIosFromUa(ua));
  }

  return parsed;
}

function pickNonEmpty(target, source, keys) {
  keys.forEach((key) => {
    const value = source[key];
    if (value != null && String(value).trim() !== "") {
      target[key] = String(value).trim();
    }
  });
}

function mergeContextParts(...parts) {
  const merged = {};
  const keys = ["device_model", "app_version_name", "os_version", "device_os"];
  parts.forEach((part) => {
    if (part && typeof part === "object") pickNonEmpty(merged, part, keys);
  });
  return merged;
}

function buildAppWebViewContext(raw = {}) {
  const clientType = resolveBehaviorClientType();
  const context = {
    in_app_webview: true,
    device_os: raw.device_os || (clientType === "ios" ? "ios" : "android"),
  };
  pickNonEmpty(context, raw, ["device_model", "app_version_name", "os_version"]);
  return context;
}

function isAppWebViewEvent(event) {
  if (!event) return false;
  if (event.client_type === "android" || event.client_type === "ios") return true;
  return event.context?.in_app_webview === true;
}

function isAppWebViewContextComplete(context = {}) {
  return Boolean(context.device_model && context.app_version_name && context.os_version);
}

function mergeAppWebViewContext(existing = {}, resolved = {}) {
  const merged = { ...existing };
  const keys = [
    "device_os",
    "os_version",
    "device_model",
    "app_version_name",
    "locale",
    "network_type",
    "in_app_webview",
  ];
  keys.forEach((key) => {
    const value = existing[key];
    const resolvedValue = resolved[key];
    if ((value == null || value === "") && resolvedValue != null && resolvedValue !== "") {
      merged[key] = resolvedValue;
    }
  });
  if (merged.in_app_webview !== true) merged.in_app_webview = true;
  return merged;
}

/**
 * 出队/上报前修补 App WebView 事件 context；无法补齐必填字段则丢弃，避免 400 重试死循环。
 * @returns {object|null} 修补后事件；null 表示应移出队列
 */
export function patchAppWebViewEventContext(event) {
  if (!isAppWebViewEvent(event)) return event;

  const resolved = isInAppWebView() ? resolveAppWebViewBehaviorContext() : {};
  const context = mergeAppWebViewContext(event.context || {}, resolved);

  if (!isAppWebViewContextComplete(context)) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "[behaviorTracker] drop unfixable app webview event",
        event.event_name,
        context,
      );
    }
    return null;
  }

  return { ...event, context };
}

/**
 * App 原生可在启动时调用，WebView 内各 H5 页复用同一套设备 context。
 * @param {object} context
 */
export function setAppWebViewBehaviorContext(context) {
  if (typeof window === "undefined" || !context || typeof context !== "object") return;
  const normalized = buildAppWebViewContext(context);
  window.__BEHAVIOR_APP_CONTEXT__ = normalized;
  writeStorageContext(normalized);
  return normalized;
}

/**
 * App WebView 必填 context：device_model / app_version_name / os_version 等。
 * 优先级：App 注入 > session/localStorage > JSBridge > UA 解析。
 */
export function resolveAppWebViewBehaviorContext() {
  if (!isInAppWebView()) return {};

  const cached = readStorageContext();
  if (cached && cached.device_model && cached.app_version_name) {
    return buildAppWebViewContext(cached);
  }

  const merged = mergeContextParts(
    readInjectedContext(),
    cached,
    readBridgeContext(),
    parseFromUserAgent(),
  );

  const context = buildAppWebViewContext(merged);
  if (context.device_model || context.app_version_name) {
    writeStorageContext(context);
  }
  return context;
}

if (typeof window !== "undefined") {
  window.setBehaviorAppContext = setAppWebViewBehaviorContext;
}
